/**
 * The address control's query path: one debounced query fanned out to Google Places and to every
 * configured record source, with each group landing on its own. Consumed by `useAddressUI`, which
 * owns the write path and passes `searchAddresses` in from its single `useGoogleMapsPlaces`
 * instance — a second instance would open a second billing session over the same keystrokes.
 *
 * The query sequence is claimed by the keystroke and compared inside the functional updater at every
 * landing, so a slower earlier answer can never overwrite a later one. It is the only counter this
 * hook touches: nothing here calls `onChange`, and the write sequence is invisible from here.
 * Source groups sit parallel to `recordSources` by index and are never keyed — two configured
 * sources may name the same entity and the same property.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import debounce from 'lodash/debounce';
import type { AddressComponents, AddressRecordSource, StreetAddress } from '#types';
import { DEFAULT_SEARCH_DEBOUNCE_MS, DEFAULT_SEARCH_MIN_CHARS } from '#types';
import { searchEntityRecords, AlgoliaRecordHit } from '@/services/searchEntityRecords';
import { pulledRecordAddress } from './pulledRecordAddress';
import type { AddressOption, AddressOptionGroup } from './types';

/** Places (New) returns nothing useful below three characters, and bills the attempt. */
const PLACES_MIN_CHARS = 3;

/**
 * Rows asked of each source per keystroke. Far below `DEFAULT_SEARCH_HITS_PER_PAGE`, which sizes a
 * browsable page: here every configured source renders its own group above Google's five
 * predictions, each row two lines tall, so a page-sized answer from three sources would bury the
 * addresses under a hundred and fifty rows nobody scrolls to.
 */
const RECORD_SOURCE_HITS_PER_PAGE = 8;

const PLACES_GROUP_LABEL = 'Addresses';

/** The one row a source that could not be reached renders, so an outage never looks like no match. */
const SOURCE_UNAVAILABLE_LABEL = 'Could not search';

type SourceSearchGroup = {
  source: AddressRecordSource;
  rows: AlgoliaRecordHit[];
  pending: boolean;
  failed: boolean;
};

type AddressSearchResults = {
  seq: number;
  query: string;
  places: { rows: StreetAddress[]; pending: boolean };
  /**
   * Parallel to `recordSources` by index, never keyed: an administrator may configure two sources
   * naming the same entity and the same property, so no key derived from a source is unique. The
   * index is what tells them apart, in the state, in the option `value` and in the landing.
   */
  sources: readonly SourceSearchGroup[];
};

/** Places answers only a query of its own minimum length; a record source answers a shorter one. */
const asksPlaces = (query: string): boolean => query.length >= PLACES_MIN_CHARS;

const asksRecords = (query: string, recordSources: readonly AddressRecordSource[]): boolean =>
  recordSources.length > 0 && query.trim().length >= DEFAULT_SEARCH_MIN_CHARS;

const emptyResults = (
  seq: number,
  recordSources: readonly AddressRecordSource[]
): AddressSearchResults => ({
  seq,
  query: '',
  places: { rows: [], pending: false },
  sources: recordSources.map((source) => ({ source, rows: [], pending: false, failed: false })),
});

/**
 * The immutable index replace a landing applies. `Array.prototype.with` throws `RangeError` out of
 * range, and this runs inside a `setResults` updater, so the throw would surface as a crash in
 * React's render rather than a dropped result. The `prev.seq === seq` guard at every call site is
 * what keeps `index` in range: a dispatch that rebuilt `sources` against a shorter `recordSources`
 * also advanced the sequence, so its landings are already stale. Rebuilding `sources` without
 * advancing the sequence turns a stale landing into that crash.
 */
const withSource = (
  results: AddressSearchResults,
  index: number,
  landed: Pick<SourceSearchGroup, 'rows' | 'pending' | 'failed'>
): AddressSearchResults => ({
  ...results,
  sources: results.sources.with(index, { ...results.sources[index], ...landed }),
});

/**
 * A hit as one dropdown row. A hit carrying `fields` has already told us whether the named property
 * holds an address; one without them was truncated by Algolia, so the row stays selectable and
 * `handleSelect` decides after reading the record.
 */
const recordOption = (
  hit: AlgoliaRecordHit,
  source: AddressRecordSource,
  sourceIndex: number,
  componentKeys: readonly (keyof AddressComponents)[]
): AddressOption => {
  const address = hit.fields
    ? pulledRecordAddress(hit.fields[source.fieldId]?.value, componentKeys)
    : undefined;
  return {
    optionKind: 'record',
    value: `record:${sourceIndex}:${hit.objectID}`,
    label: hit.name || hit.objectID,
    source,
    recordId: hit.objectID,
    address,
    disabled: !!hit.fields && !address,
  };
};

export const useAddressSearch = ({
  recordSources,
  componentKeys,
  searchAddresses,
}: {
  recordSources: readonly AddressRecordSource[];
  componentKeys: readonly (keyof AddressComponents)[];
  searchAddresses: (query: string) => Promise<StreetAddress[]>;
}) => {
  const querySequenceRef = useRef(0);
  const [results, setResults] = useState<AddressSearchResults>(() => emptyResults(0, recordSources));

  /**
   * Both sides settle their own group, success or failure. `searchAddresses` swallows its errors
   * into `[]` today, but it arrives here as a parameter: a caller-supplied function that rejects
   * would otherwise leave Places pending for good, and the dropdown reading "Searching..." until
   * the next keystroke.
   */
  const fetchAll = useCallback((query: string, seq: number) => {
    if (asksPlaces(query)) {
      void searchAddresses(query).then(
        (rows) =>
          setResults((prev) => (prev.seq === seq ? { ...prev, places: { rows, pending: false } } : prev)),
        () =>
          setResults((prev) => (prev.seq === seq ? { ...prev, places: { rows: [], pending: false } } : prev))
      );
    }
    if (asksRecords(query, recordSources)) {
      recordSources.forEach((source, index) => {
        void searchEntityRecords({
          entityId: source.entityId,
          query,
          hitsPerPage: RECORD_SOURCE_HITS_PER_PAGE,
        }).then(
          (rows) =>
            setResults((prev) =>
              prev.seq === seq ? withSource(prev, index, { rows, pending: false, failed: false }) : prev
            ),
          () =>
            setResults((prev) =>
              prev.seq === seq ? withSource(prev, index, { rows: [], pending: false, failed: true }) : prev
            )
        );
      });
    }
  }, [recordSources, searchAddresses]);

  /**
   * The timer's target, kept current so the timer itself never has to be rebuilt. Rebuilding it
   * would cancel whatever keystroke is already waiting, and `recordSources` is a prop: a caller
   * passing `sources ?? []` or a `.map()` would hand us a new array every render and no keystroke
   * would ever reach a source.
   */
  const fetchAllRef = useRef(fetchAll);
  useEffect(() => { fetchAllRef.current = fetchAll; }, [fetchAll]);

  const debouncedFetch = useMemo(
    () => debounce((query: string, seq: number) => fetchAllRef.current(query, seq), DEFAULT_SEARCH_DEBOUNCE_MS),
    []
  );

  /**
   * The timer is an external system, so cancelling it is the one thing this hook subscribes for.
   * No sequence is advanced on unmount: a landing compares against state through a functional
   * update, and a setState after unmount is a no-op.
   */
  useEffect(() => () => { debouncedFetch.cancel(); }, [debouncedFetch]);

  const abandon = useCallback(() => {
    debouncedFetch.cancel();
    querySequenceRef.current += 1;
    const seq = querySequenceRef.current;
    setResults((prev) => ({
      seq,
      query: '',
      places: { rows: [], pending: false },
      sources: prev.sources.map((group) => ({ ...group, rows: [], pending: false, failed: false })),
    }));
  }, [debouncedFetch]);

  /**
   * Claims the query sequence on the keystroke, not on the fetch that follows the debounce: the
   * moment the person types, every answer already in flight is stale. Each side is marked pending
   * only when the query reaches its own threshold, and a group keeps the rows it last landed while
   * its next answer is outstanding, so the dropdown never empties between keystrokes. Places is the
   * exception: below its threshold it holds no rows at all rather than predictions for a longer
   * query the person has since deleted. A query neither side will answer abandons the search
   * instead, which is the only moment the dropdown closes for want of options.
   */
  const search = useCallback((query: string) => {
    const searchesPlaces = asksPlaces(query);
    const searchesRecords = asksRecords(query, recordSources);
    if (!searchesPlaces && !searchesRecords) {
      abandon();
      return;
    }
    querySequenceRef.current += 1;
    const seq = querySequenceRef.current;
    setResults((prev) => ({
      seq,
      query,
      places: searchesPlaces ? { rows: prev.places.rows, pending: true } : { rows: [], pending: false },
      sources: recordSources.map((source, index) => ({
        source,
        rows: prev.sources[index]?.source === source ? prev.sources[index].rows : [],
        pending: searchesRecords,
        failed: false,
      })),
    }));
    debouncedFetch(query, seq);
  }, [abandon, debouncedFetch, recordSources]);

  const options = useMemo((): (AddressOption | AddressOptionGroup)[] => {
    const placesOptions: AddressOption[] = results.places.rows
      .filter((prediction): prediction is StreetAddress & { formatted: string } => !!prediction.formatted)
      .map((prediction, index) => ({
        optionKind: 'places',
        value: `places:${prediction.alternativeData?.placeId ?? index}`,
        label: prediction.formatted,
        prediction,
      }));

    if (recordSources.length === 0) return placesOptions;

    const placesGroups: AddressOptionGroup[] = placesOptions.length > 0
      ? [{ optionKind: 'group', label: PLACES_GROUP_LABEL, options: placesOptions }]
      : [];

    const sourceGroups: AddressOptionGroup[] = results.sources.flatMap((group, index) => {
      if (group.failed) {
        return [{
          optionKind: 'group',
          label: group.source.label,
          options: [{
            optionKind: 'unavailable',
            value: `unavailable:${index}`,
            label: SOURCE_UNAVAILABLE_LABEL,
            disabled: true,
          }],
        }];
      }
      if (group.rows.length === 0) return [];
      return [{
        optionKind: 'group',
        label: group.source.label,
        options: group.rows.map((hit) => recordOption(hit, group.source, index, componentKeys)),
      }];
    });

    return [...placesGroups, ...sourceGroups];
  }, [results, recordSources, componentKeys]);

  return {
    options,
    isSearching: results.places.pending || results.sources.some((group) => group.pending),
    hasRows: options.length > 0,
    /** A query is standing: one was dispatched at or above `lowestThreshold` and not yet abandoned. */
    hasQuery: results.query !== '',
    search,
    abandon,
  };
};
